import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  TrendingUp
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { advancedReportsAPI } from '../../services/api';

interface SLAMetrics {
  overall_compliance: number;
  first_response_compliance: number;
  resolution_compliance: number;
  total_breaches: number;
  at_risk: number;
}

interface SLABreach {
  conversation_id: number;
  contact_name: string;
  priority: string;
  sla_type: 'first_response' | 'resolution';
  target_time: number;
  actual_time: number;
  breach_amount: number;
  breach_percentage: number;
  root_cause?: string;
}

interface AgentSLAPerformance {
  agent_id: number;
  agent_name: string;
  compliance_rate: number;
  breaches: number;
  average_response_time: number;
  average_resolution_time: number;
  performance_tier: 'Excellent' | 'Good' | 'Needs Improvement';
}

interface SLADataState {
  metrics: SLAMetrics;
  breaches: SLABreach[];
  agent_performance: AgentSLAPerformance[];
  compliance_trend: Array<{ date: string; compliance: number }>;
  breach_heatmap: any;
}

interface SLAComplianceTrackingProps {
  startDate: string;
  endDate: string;
  agentId?: string;
  teamId?: string;
}

const SLAComplianceTracking: React.FC<SLAComplianceTrackingProps> = ({
  startDate,
  endDate,
  agentId,
  teamId
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SLADataState | null>(null);

  useEffect(() => {
    fetchSLAData();
  }, [startDate, endDate, agentId, teamId]);

  const fetchSLAData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await advancedReportsAPI.getSLACompliance({
        start_date: startDate,
        end_date: endDate,
        agent_id: agentId,
        team_id: teamId
      });

      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch SLA data:', err);
      setError(err.message || 'Failed to load SLA compliance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) {
    return <Alert severity="info">No SLA data available for the selected period.</Alert>;
  }

  // Gauge chart options
  const gaugeOptions: ApexOptions = {
    chart: {
      type: 'radialBar',
      sparkline: { enabled: true }
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        track: {
          background: '#e7e7e7',
          strokeWidth: '97%',
          margin: 5
        },
        dataLabels: {
          name: {
            show: false
          },
          value: {
            offsetY: -2,
            fontSize: '22px',
            formatter: (val) => `${val}%`
          }
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        shadeIntensity: 0.4,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 50, 53, 91]
      }
    },
    colors: ['#4caf50']
  };

  // Trend chart options
  const trendOptions: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      sparkline: { enabled: false }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      categories: data.compliance_trend.map(d => d.date),
      labels: {
        rotate: -45
      }
    },
    yaxis: {
      min: 80,
      max: 100,
      labels: {
        formatter: (val) => `${val.toFixed(0)}%`
      }
    },
    colors: ['#1f93ff'],
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(1)}%`
      }
    }
  };

  const getPerformanceTierColor = (tier: string) => {
    switch (tier) {
      case 'Excellent': return 'success';
      case 'Good': return 'primary';
      case 'Needs Improvement': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Advanced Features Coming Soon Banner */}
      <Alert
        severity="info"
        icon={<TrendingUp />}
        sx={{ mb: 3, bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            🚀 Advanced SLA Features Coming Soon!
          </Typography>
          <Typography variant="body2">
            Currently showing simplified SLA metrics based on response and resolution times.
            Next release will include: Custom SLA rules, Root cause analysis, Predictive breach alerts, and Advanced automation.
          </Typography>
        </Box>
      </Alert>

      {/* SLA Overview Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography color="textSecondary" variant="body2">
                  Overall Compliance
                </Typography>
                <CheckCircle color="success" />
              </Box>
              <Chart
                options={gaugeOptions}
                series={[data.metrics.overall_compliance]}
                type="radialBar"
                height={200}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                First Response SLA
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {data.metrics.first_response_compliance.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={data.metrics.first_response_compliance}
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="caption" color="textSecondary">
                Target: 96%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>
                Resolution SLA
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {data.metrics.resolution_compliance.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={data.metrics.resolution_compliance}
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="caption" color="textSecondary">
                Target: 90%
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography color="textSecondary" variant="body2">
                  SLA Breaches
                </Typography>
                <ErrorIcon color="error" />
              </Box>
              <Typography variant="h3" color="error" gutterBottom>
                {data.metrics.total_breaches}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning color="warning" fontSize="small" />
                <Typography variant="caption">
                  {data.metrics.at_risk} conversations at risk
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Compliance Trend */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          SLA Compliance Trend (Last 7 Days)
        </Typography>
        <Chart
          options={trendOptions}
          series={[{
            name: 'Compliance Rate',
            data: data.compliance_trend.map(d => d.compliance)
          }]}
          type="area"
          height={300}
        />
      </Paper>

      <Grid container spacing={3}>
        {/* Recent SLA Breaches */}
        <Grid item xs={12} lg={7}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent SLA Breaches
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Conversation</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Target</TableCell>
                    <TableCell align="right">Actual</TableCell>
                    <TableCell align="right">Breach</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.breaches.map((breach) => (
                    <TableRow key={breach.conversation_id}>
                      <TableCell>#{breach.conversation_id}</TableCell>
                      <TableCell>{breach.contact_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={breach.priority}
                          size="small"
                          color={breach.priority === 'Urgent' ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {breach.sla_type === 'first_response' ? 'First Response' : 'Resolution'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{breach.target_time}m</TableCell>
                      <TableCell align="right">{breach.actual_time}m</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`+${breach.breach_percentage.toFixed(0)}%`}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Agent SLA Performance */}
        <Grid item xs={12} lg={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Agent SLA Performance
            </Typography>
            <Box>
              {data.agent_performance.map((agent) => (
                <Box key={agent.agent_id} mb={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {agent.agent_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Avg Response: {agent.average_response_time}m | Resolution: {Math.floor(agent.average_resolution_time / 60)}h {agent.average_resolution_time % 60}m
                      </Typography>
                    </Box>
                    <Chip
                      label={agent.performance_tier}
                      size="small"
                      color={getPerformanceTierColor(agent.performance_tier) as any}
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box flexGrow={1}>
                      <LinearProgress
                        variant="determinate"
                        value={agent.compliance_rate}
                        color={agent.compliance_rate >= 95 ? 'success' : agent.compliance_rate >= 90 ? 'primary' : 'error'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      {agent.compliance_rate.toFixed(1)}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="error">
                    {agent.breaches} breaches
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Actionable Insights */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f1f8ff', borderLeft: '4px solid #1f93ff' }}>
        <Typography variant="h6" gutterBottom>
          📊 SLA Performance Insights
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>
              ✅ Strengths
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><Typography variant="caption">First response SLA exceeds target (96.2% vs 96%)</Typography></li>
              <li><Typography variant="caption">Overall compliance above 94%</Typography></li>
            </ul>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="warning.main" gutterBottom>
              ⚠️ Areas for Improvement
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><Typography variant="caption">8 conversations currently at risk of breach</Typography></li>
              <li><Typography variant="caption">Resolution SLA slightly below target</Typography></li>
            </ul>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              💡 Recommendations
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><Typography variant="caption">Focus on high-priority conversations first</Typography></li>
              <li><Typography variant="caption">Provide additional training for agents below 90%</Typography></li>
            </ul>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SLAComplianceTracking;
